import { gql } from "graphql-request";
import { z } from "zod";
import { Image, ImageFragment } from "../image";
import { PollBaseSchema } from "./carouselEventPollEntry";

export const TextBlockSchema = z.object({
  __typename: z.literal("TextBlock"),
  title: z.string().nullable(),
  text: z
    .object({
      json: z.unknown(),
    })
    .nullable(),
  accordion: z.enum(["yes", "no"]).nullable(),
});

export const TextBlock = TextBlockSchema;
export type TextBlock = z.infer<typeof TextBlockSchema>;

export const TextBlockFragment = gql`
  fragment TextBlockFragment on TextBlock {
    title
    text {
      json
    }
    accordion
  }
`;

export const ImageBlockSchema = z.object({
  __typename: z.literal("ImageBlock"),
  image: Image.nullable(),
  imageEn: Image.nullable(),
  author: z.string().nullable(),
  fullWidth: z.enum(["yes", "no"]).nullable(),
});

export const ImageBlock = ImageBlockSchema;
export type ImageBlock = z.infer<typeof ImageBlockSchema>;

export const ImageBlockFragment = gql`
  fragment ImageBlockFragment on ImageBlock {
    image {
      ...ImageFragment
    }
    imageEn: image(locale: "en") {
      ...ImageFragment
    }
    author
    fullWidth
  }

  ${ImageFragment}
`;

export const FeatureBlockSchema = z.object({
  __typename: z.literal("FeatureBlock"),
  title: z.string().nullable(),
  featureText: z
    .object({
      json: z.unknown(),
    })
    .nullable(),
  featureThumbnail: Image.nullable(),
  featureThumbnailEn: Image.nullable(),
  featureThumbnailPosition: z.enum(["left", "right"]).nullable(),
  accordion: z.enum(["yes", "no"]).nullable(),
});

export const FeatureBlock = FeatureBlockSchema;
export type FeatureBlock = z.infer<typeof FeatureBlockSchema>;

export const FeatureBlockFragment = gql`
  fragment FeatureBlockFragment on FeatureBlock {
    title
    featureText {
      json
    }
    featureThumbnail {
      ...ImageFragment
    }
    featureThumbnailEn: featureThumbnail(locale: "en") {
      ...ImageFragment
    }
    featureThumbnailPosition
    accordion
  }
  ${ImageFragment}
`;

export const VideoBlockSchema = z.object({
  __typename: z.literal("VideoBlock"),
  title: z.string().nullable(),
  videoUrl: z.string().nullable(),
  videoThumbnail: Image.nullable(),
  videoThumbnailEn: Image.nullable(),
});

export const VideoBlock = VideoBlockSchema;
export type VideoBlock = z.infer<typeof VideoBlockSchema>;

export const VideoBlockFragment = gql`
  fragment VideoBlockFragment on VideoBlock {
    title
    videoUrl
    videoThumbnail {
      ...ImageFragment
    }
    videoThumbnailEn: videoThumbnail(locale: "en") {
      ...ImageFragment
    }
  }

  ${ImageFragment}
`;

export const PollBlockSchema = PollBaseSchema.extend({
  __typename: z.literal("PollBlock"),
});

export const PollBlock = PollBlockSchema;
export type PollBlock = z.infer<typeof PollBlockSchema>;

export const PollBlockFragment = gql`
  fragment PollBlockFragment on PollBlock {
    pollTitle

    pollOptions
  }

  ${ImageFragment}
`;

const LeaderboardColumnSchema = z.object({
  id: z.string(),
  title: z.string(),
  showColumnTitle: z.boolean().optional(),
  color: z.string().optional(),
  image: z
    .object({
      id: z.string(),
      title: z.string(),
      width: z.number(),
      height: z.number(),
      url: z.string(),
    })
    .optional(),
});

export const LeaderboardConfigurationSchema = z
  .object({
    useRankColumn: z.boolean(),
    columns: z.array(LeaderboardColumnSchema),
  })
  .nullable();

export const LeaderboardConfiguration = LeaderboardConfigurationSchema;
export type LeaderboardConfiguration = z.infer<
  typeof LeaderboardConfigurationSchema
>;

export const LeaderboardBlockSchema = z.object({
  __typename: z.literal("LeaderboardBlock"),
  title: z.string().nullable(),
  defaultRows: z.number().nullable(),
  maximumRows: z.number().nullable(),
  leaderboardConfiguration: LeaderboardConfigurationSchema,
  leaderboardData: z.array(z.array(z.string().or(z.number()))).nullable(),
});

export const LeaderboardBlock = LeaderboardBlockSchema;
export type LeaderboardBlock = z.infer<typeof LeaderboardBlockSchema>;

export const LeaderboardBlockFragment = gql`
  fragment LeaderboardBlockFragment on LeaderboardBlock {
    title
    defaultRows
    maximumRows
    leaderboardConfiguration
    leaderboardData
  }

  ${ImageFragment}
`;
